// ============================================================================
// Zona de arrastre accesible: clic, Enter/Espacio o arrastrar un PDF/JPG/PNG/XML
// de hasta 10 MB. Muestra el archivo elegido y la barra de progreso de la subida.
// ============================================================================
import { useId, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../components/ui";
import { ACCEPT_ATTR, DOCS_NS, formatBytes, validateUploadFile } from "./meta";

interface UploadDropzoneProps {
  file: File | null;
  onFile: (file: File | null) => void;
  progress?: number | null;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

export function UploadDropzone({ file, onFile, progress = null, disabled = false, compact = false, className }: UploadDropzoneProps) {
  const { t } = useTranslation(DOCS_NS);
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hintId = useId();

  const pick = (next: File | null) => {
    if (!next) {
      onFile(null);
      setError(null);
      return;
    }
    const verdict = validateUploadFile(next);
    if (verdict === "type") {
      setError(t("checklist.upload.invalidType"));
      onFile(null);
      return;
    }
    if (verdict === "size") {
      setError(t("checklist.upload.tooLarge"));
      onFile(null);
      return;
    }
    setError(null);
    onFile(next);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    const dropped = e.dataTransfer?.files?.[0] ?? null;
    pick(dropped);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  const uploading = progress !== null && progress !== undefined && progress >= 0 && progress < 100;

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-describedby={hintId}
        aria-label={t("checklist.upload.drop")}
        data-testid="document-dropzone"
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={onKey}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed text-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-500",
          compact ? "px-3 py-3" : "px-4 py-6",
          over ? "border-primary-500 bg-primary-500/10" : "border-line bg-app hover:border-primary-500/60",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <i className={cn(file ? "ri-file-check-line text-success" : "ri-upload-cloud-2-line text-primary-500", compact ? "text-xl" : "text-3xl")} />
        {file ? (
          <>
            <span className="max-w-full truncate text-sm font-medium text-content">{file.name}</span>
            <span className="text-[11px] text-content-muted">{formatBytes(file.size)}</span>
          </>
        ) : (
          <>
            <span className="text-sm font-medium text-content">{t("checklist.upload.drop")}</span>
            <span className="text-xs text-primary-500">{t("checklist.upload.browse")}</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          className="sr-only"
          tabIndex={-1}
          disabled={disabled}
          onChange={(e) => pick(e.target.files?.[0] ?? null)}
          data-testid="document-file-input"
        />
      </div>
      <p id={hintId} className="mt-1 text-[11px] text-content-muted">
        {t("checklist.upload.hint")}
      </p>
      {error ? (
        <p role="alert" className="mt-1 text-xs text-error">
          {error}
        </p>
      ) : null}
      {uploading ? (
        <div className="mt-2" role="progressbar" aria-valuenow={progress ?? 0} aria-valuemin={0} aria-valuemax={100} aria-label={t("checklist.upload.uploading")}>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-subtle">
            <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${progress ?? 0}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-content-muted">
            {t("checklist.upload.uploading")} · {progress}%
          </div>
        </div>
      ) : null}
      {file ? (
        <button type="button" onClick={() => pick(null)} disabled={disabled || uploading} className="mt-1 text-xs text-content-secondary underline-offset-2 hover:underline">
          {t("checklist.upload.clear")}
        </button>
      ) : null}
    </div>
  );
}

export default UploadDropzone;
